<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import StorageAPI from 'dashboard/api/storage';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';

const { t } = useI18n();

const cleanupConfirmDialog = ref(null);
const deduplicateConfirmDialog = ref(null);

const analyzing = ref(false);
const cleanupLoading = ref(false);
const cancellingCleanup = ref(false);
const deduplicateLoading = ref(false);
const cancellingDedup = ref(false);
const showDuplicates = ref(false);
const duplicatesLoading = ref(false);
const showLargestFiles = ref(false);
const largestFilesLoading = ref(false);

const storageData = ref(null);
const duplicatesList = ref([]);
const largestFilesList = ref([]);

const analysisStatus = ref('not_started');
const analysisError = ref(null);
let analysisPoller = null;

const cleanupStatus = ref('not_started');
let cleanupPoller = null;

const deduplicationStatus = ref('not_started');
let deduplicationPoller = null;

const hasData = computed(() => storageData.value !== null);

const formatBytes = bytes => {
  if (!bytes) return '0 B';

  const unit = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.floor(Math.log(bytes) / Math.log(unit));

  return `${parseFloat((bytes / unit ** index).toFixed(2))} ${sizes[index]}`;
};

const formatNumber = num => {
  return (num || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const totalSize = computed(() => storageData.value?.overview?.total_size || 0);
const orphanSize = computed(() => storageData.value?.orphan_blobs?.size || 0);
const duplicateWasted = computed(
  () => storageData.value?.duplicates?.wasted_space || 0
);
const potentialSavings = computed(
  () => orphanSize.value + duplicateWasted.value
);

const lastAnalyzedAt = computed(() => {
  if (!storageData.value?.analyzed_at) return null;
  return new Date(storageData.value.analyzed_at);
});

const lastAnalyzedFormatted = computed(() => {
  if (!lastAnalyzedAt.value) return '';

  const now = new Date();
  const diffMs = now - lastAnalyzedAt.value;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);

  if (diffMins < 1) return t('STORAGE_MGMT.JUST_NOW');
  if (diffMins < 60) {
    return t('STORAGE_MGMT.MINUTES_AGO', { minutes: diffMins });
  }
  if (diffHours < 2) return t('STORAGE_MGMT.HOURS_AGO', { hours: diffHours });

  return lastAnalyzedAt.value.toLocaleString();
});

const analysisIsStale = computed(() => {
  if (!lastAnalyzedAt.value) return false;
  return (new Date() - lastAnalyzedAt.value) / 3600000 > 1.5;
});

const cleanupConfirmMessage = computed(() => {
  if (!storageData.value) return '';

  return t('STORAGE_MGMT.CLEANUP_CONFIRM', {
    count: storageData.value.orphan_blobs?.count || 0,
    size: formatBytes(orphanSize.value),
  });
});

const deduplicateConfirmMessage = computed(() => {
  if (!storageData.value) return '';

  return t('STORAGE_MGMT.DEDUPLICATE_CONFIRM', {
    count: storageData.value.duplicates?.files || 0,
    size: formatBytes(duplicateWasted.value),
  });
});

const getSourceLabel = key => {
  const sourceLabels = {
    messages: 'STORAGE_MGMT.SOURCE_MESSAGES',
    canned_responses: 'STORAGE_MGMT.SOURCE_CANNED_RESPONSES',
    avatars: 'STORAGE_MGMT.SOURCE_AVATARS',
  };

  return sourceLabels[key] || 'STORAGE_MGMT.SOURCE_DEFAULT';
};

const formatDate = dateString => new Date(dateString).toLocaleDateString();

const stopAnalysisPolling = () => {
  if (analysisPoller) {
    clearInterval(analysisPoller);
    analysisPoller = null;
  }
};

const startAnalysisPolling = fn => {
  if (analysisPoller) return;
  analysisPoller = setInterval(fn, 3000);
};

const stopCleanupPolling = () => {
  if (cleanupPoller) {
    clearInterval(cleanupPoller);
    cleanupPoller = null;
  }
};

const startCleanupPolling = fn => {
  if (cleanupPoller) return;
  cleanupPoller = setInterval(fn, 3000);
};

const stopDeduplicationPolling = () => {
  if (deduplicationPoller) {
    clearInterval(deduplicationPoller);
    deduplicationPoller = null;
  }
};

const startDeduplicationPolling = fn => {
  if (deduplicationPoller) return;
  deduplicationPoller = setInterval(fn, 3000);
};

const checkAnalysisStatus = async (showAlert = false) => {
  try {
    const response = await StorageAPI.status();
    const { status, result, error } = response.data;

    analysisStatus.value = status;
    analysisError.value = error || null;

    if (status === 'completed' && result) {
      storageData.value = result;
      analyzing.value = false;
      stopAnalysisPolling();
      if (showAlert) useAlert(t('STORAGE_MGMT.ANALYZE_SUCCESS'));
    } else if (status === 'in_progress' || status === 'queued') {
      analyzing.value = true;
      if (!analysisPoller) {
        startAnalysisPolling(() => checkAnalysisStatus(false));
      }
    } else if (status === 'not_started' && analyzing.value) {
      if (!analysisPoller) {
        startAnalysisPolling(() => checkAnalysisStatus(false));
      }
    } else if (status === 'timeout' || status === 'failed') {
      analyzing.value = false;
      stopAnalysisPolling();
      if (showAlert) useAlert(error || t('STORAGE_MGMT.ANALYZE_ERROR'));
    } else {
      analyzing.value = false;
      stopAnalysisPolling();
    }
  } catch {
    analyzing.value = false;
  }
};

const analyzeStorage = async ({ force = false } = {}) => {
  if (analyzing.value) return;

  try {
    analyzing.value = true;
    analysisStatus.value = 'in_progress';
    stopAnalysisPolling();

    const response = await StorageAPI.analyze({ force });

    if (
      response.data.status === 'queued' ||
      response.data.status === 'in_progress'
    ) {
      if (force) useAlert(t('STORAGE_MGMT.ANALYZE_QUEUED'));
      startAnalysisPolling(() => checkAnalysisStatus(false));
    } else if (response.data.overview) {
      storageData.value = response.data;
      analysisStatus.value = 'completed';
      analyzing.value = false;
      if (force) useAlert(t('STORAGE_MGMT.ANALYZE_SUCCESS'));
    }
  } catch {
    analysisStatus.value = 'failed';
    analyzing.value = false;
    useAlert(t('STORAGE_MGMT.ANALYZE_ERROR'));
  }
};

const loadDuplicates = async () => {
  try {
    duplicatesLoading.value = true;
    showDuplicates.value = true;
    const response = await StorageAPI.duplicates(50);
    duplicatesList.value = response.data.duplicates || [];
  } catch {
    useAlert(t('STORAGE_MGMT.DUPLICATES_ERROR'));
  } finally {
    duplicatesLoading.value = false;
  }
};

const loadLargestFiles = async () => {
  try {
    largestFilesLoading.value = true;
    showLargestFiles.value = true;
    const response = await StorageAPI.largestFiles(20);
    largestFilesList.value = response.data.files || [];
  } catch {
    useAlert(t('STORAGE_MGMT.LARGEST_FILES_ERROR'));
  } finally {
    largestFilesLoading.value = false;
  }
};

const checkCleanupStatus = async (showAlert = true) => {
  try {
    const response = await StorageAPI.cleanupStatus();
    const {
      status,
      cleaned_count: cleanedCount,
      space_freed: spaceFreed,
    } = response.data;

    cleanupStatus.value = status;

    if (status === 'completed') {
      cleanupLoading.value = false;
      cancellingCleanup.value = false;
      stopCleanupPolling();
      if (showAlert) {
        useAlert(
          t('STORAGE_MGMT.CLEANUP_SUCCESS', {
            count: cleanedCount,
            size: formatBytes(spaceFreed),
          })
        );
      }
      await analyzeStorage({ force: true });
    } else if (status === 'in_progress') {
      cleanupLoading.value = true;
      if (!cleanupPoller) {
        startCleanupPolling(() => checkCleanupStatus(false));
      }
    } else if (status === 'interrupted') {
      cleanupLoading.value = false;
      cancellingCleanup.value = false;
      stopCleanupPolling();
      if (showAlert) useAlert(t('STORAGE_MGMT.CLEANUP_INTERRUPTED'));
    } else if (status === 'cancelled') {
      cleanupLoading.value = false;
      cancellingCleanup.value = false;
      stopCleanupPolling();
      if (showAlert) useAlert(t('STORAGE_MGMT.CLEANUP_CANCELLED'));
      await analyzeStorage({ force: true });
    } else if (status === 'error') {
      cleanupLoading.value = false;
      cancellingCleanup.value = false;
      stopCleanupPolling();
      if (showAlert) useAlert(t('STORAGE_MGMT.CLEANUP_ERROR'));
    } else {
      cleanupLoading.value = false;
      cancellingCleanup.value = false;
      stopCleanupPolling();
    }
  } catch {
    cleanupLoading.value = false;
    cancellingCleanup.value = false;
  }
};

const cleanupOrphans = async () => {
  const confirmed = await cleanupConfirmDialog.value.showConfirmation();
  if (!confirmed) return;

  try {
    cleanupLoading.value = true;
    cleanupStatus.value = 'in_progress';
    await StorageAPI.cleanupOrphans();
    useAlert(t('STORAGE_MGMT.CLEANUP_QUEUED'));
    startCleanupPolling(() => checkCleanupStatus(false));
  } catch {
    cleanupLoading.value = false;
    cleanupStatus.value = 'failed';
    useAlert(t('STORAGE_MGMT.CLEANUP_ERROR'));
  }
};

const cancelCleanup = async () => {
  try {
    cancellingCleanup.value = true;
    await StorageAPI.cancelCleanup();
    useAlert(t('STORAGE_MGMT.CANCEL_CLEANUP_REQUESTED'));
  } catch {
    cancellingCleanup.value = false;
    useAlert(t('STORAGE_MGMT.CANCEL_CLEANUP_ERROR'));
  }
};

const checkDeduplicationStatus = async (showAlert = true) => {
  try {
    const response = await StorageAPI.deduplicationStatus();
    const {
      status,
      deduplicated_count: deduplicatedCount,
      space_saved: spaceSaved,
    } = response.data;

    deduplicationStatus.value = status;

    if (status === 'completed') {
      deduplicateLoading.value = false;
      cancellingDedup.value = false;
      stopDeduplicationPolling();
      if (showAlert) {
        useAlert(
          t('STORAGE_MGMT.DEDUPLICATE_SUCCESS', {
            count: deduplicatedCount,
            size: formatBytes(spaceSaved),
          })
        );
      }
      await analyzeStorage({ force: true });
      if (showDuplicates.value) await loadDuplicates();
    } else if (status === 'in_progress') {
      deduplicateLoading.value = true;
      if (!deduplicationPoller) {
        startDeduplicationPolling(() => checkDeduplicationStatus(false));
      }
    } else if (status === 'interrupted') {
      deduplicateLoading.value = false;
      cancellingDedup.value = false;
      stopDeduplicationPolling();
      if (showAlert) useAlert(t('STORAGE_MGMT.DEDUPLICATE_INTERRUPTED'));
    } else if (status === 'cancelled') {
      deduplicateLoading.value = false;
      cancellingDedup.value = false;
      stopDeduplicationPolling();
      if (showAlert) useAlert(t('STORAGE_MGMT.DEDUPLICATE_CANCELLED'));
      await analyzeStorage({ force: true });
      if (showDuplicates.value) await loadDuplicates();
    } else if (status === 'error') {
      deduplicateLoading.value = false;
      cancellingDedup.value = false;
      stopDeduplicationPolling();
      if (showAlert) useAlert(t('STORAGE_MGMT.DEDUPLICATE_ERROR'));
    } else {
      deduplicateLoading.value = false;
      cancellingDedup.value = false;
      stopDeduplicationPolling();
    }
  } catch {
    deduplicateLoading.value = false;
    cancellingDedup.value = false;
  }
};

const deduplicateFiles = async () => {
  const confirmed = await deduplicateConfirmDialog.value.showConfirmation();
  if (!confirmed) return;

  try {
    deduplicateLoading.value = true;
    deduplicationStatus.value = 'in_progress';
    await StorageAPI.deduplicate();
    useAlert(t('STORAGE_MGMT.DEDUPLICATE_QUEUED'));
    startDeduplicationPolling(() => checkDeduplicationStatus(false));
  } catch {
    deduplicateLoading.value = false;
    deduplicationStatus.value = 'failed';
    useAlert(t('STORAGE_MGMT.DEDUPLICATE_ERROR'));
  }
};

const cancelDeduplication = async () => {
  try {
    cancellingDedup.value = true;
    await StorageAPI.cancelDeduplication();
    useAlert(t('STORAGE_MGMT.CANCEL_DEDUP_REQUESTED'));
  } catch {
    cancellingDedup.value = false;
    useAlert(t('STORAGE_MGMT.CANCEL_DEDUP_ERROR'));
  }
};

onMounted(() => {
  checkAnalysisStatus(false);
  checkCleanupStatus(false);
  checkDeduplicationStatus(false);
});

onUnmounted(() => {
  stopAnalysisPolling();
  stopCleanupPolling();
  stopDeduplicationPolling();
});
</script>

<template>
  <SettingsLayout :is-loading="analyzing && !hasData" :no-records-found="false">
    <template #header>
      <BaseSettingsHeader
        :title="$t('STORAGE_MGMT.TITLE')"
        :description="$t('STORAGE_MGMT.DESCRIPTION')"
      >
        <template #actions>
          <NextButton
            :disabled="analyzing"
            size="small"
            blue
            @click="analyzeStorage({ force: true })"
          >
            <span v-if="!analyzing">{{ $t('STORAGE_MGMT.REFRESH') }}</span>
            <Spinner v-else size="small" />
          </NextButton>
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div
        v-if="hasData && lastAnalyzedAt"
        class="mb-4 rounded-md border bg-n-solid-1 p-3"
        :class="
          analysisIsStale ? 'border-n-amber-6 bg-n-amber-2' : 'border-n-weak'
        "
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <span
              class="flex h-6 w-6 items-center justify-center rounded-full"
              :class="
                analysisIsStale
                  ? 'bg-n-amber-3 text-n-amber-11'
                  : 'bg-n-green-3 text-n-green-11'
              "
            >
              <span
                class="h-4 w-4"
                :class="
                  analysisIsStale ? 'i-lucide-clock' : 'i-lucide-check-circle'
                "
              />
            </span>
            <div>
              <span
                class="text-sm font-medium"
                :class="analysisIsStale ? 'text-n-amber-11' : 'text-n-slate-12'"
              >
                {{ $t('STORAGE_MGMT.LAST_ANALYZED') }}:
                {{ lastAnalyzedFormatted }}
              </span>
              <span v-if="analysisIsStale" class="ml-2 text-xs text-n-amber-10">
                {{ $t('STORAGE_MGMT.DATA_MAY_BE_OUTDATED') }}
              </span>
            </div>
          </div>
          <span
            v-if="storageData.analysis_duration_seconds"
            class="text-xs text-n-slate-11"
          >
            {{
              $t('STORAGE_MGMT.ANALYSIS_TOOK', {
                seconds: storageData.analysis_duration_seconds.toFixed(1),
              })
            }}
          </span>
        </div>
      </div>

      <div
        v-if="analysisStatus === 'in_progress' || analysisStatus === 'queued'"
        class="mb-6 rounded-md border border-n-iris-6 bg-n-iris-2 p-4"
      >
        <div class="flex items-center gap-3">
          <Spinner size="small" />
          <div>
            <div class="text-sm font-medium text-n-iris-11">
              {{ $t('STORAGE_MGMT.ANALYSIS_IN_PROGRESS') }}
            </div>
            <div class="mt-1 text-xs text-n-iris-10">
              {{ $t('STORAGE_MGMT.ANALYSIS_IN_PROGRESS_DESC') }}
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="!hasData && analysisStatus === 'not_started'"
        class="mb-6 rounded-md border border-n-weak bg-n-solid-1 p-6 text-center"
      >
        <div class="text-n-slate-11">
          <div class="mb-2 text-base font-medium text-n-slate-12">
            {{ $t('STORAGE_MGMT.NO_DATA_TITLE') }}
          </div>
          <div class="text-sm">
            {{ $t('STORAGE_MGMT.NO_DATA_DESC') }}
          </div>
          <NextButton
            class="mt-4"
            size="small"
            blue
            @click="analyzeStorage({ force: true })"
          >
            {{ $t('STORAGE_MGMT.START_ANALYSIS') }}
          </NextButton>
        </div>
      </div>

      <div
        v-if="analysisStatus === 'failed' || analysisStatus === 'timeout'"
        class="mb-6 rounded-md border border-n-ruby-6 bg-n-ruby-2 p-4"
      >
        <div class="mb-1 text-sm font-medium text-n-ruby-11">
          {{ $t('STORAGE_MGMT.ANALYSIS_ERROR_TITLE') }}
        </div>
        <div class="text-xs text-n-ruby-10">
          {{ analysisError || $t('STORAGE_MGMT.ANALYZE_ERROR') }}
        </div>
      </div>

      <div v-if="hasData" class="flex flex-col gap-6">
        <div
          class="flex w-full flex-col divide-y divide-n-weak rounded-xl bg-n-solid-2 outline outline-1 outline-n-container"
        >
          <div class="flex flex-col items-start gap-2 px-5 py-4">
            <h3 class="text-base font-medium text-n-slate-12">
              {{ $t('STORAGE_MGMT.OVERVIEW_TITLE') }}
            </h3>
            <p class="mb-0 text-sm text-n-slate-11">
              {{ $t('STORAGE_MGMT.OVERVIEW_SUBTITLE') }}
            </p>
          </div>

          <div class="px-5 py-4">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div class="rounded-md border border-n-weak bg-n-solid-1 p-4">
                <div class="text-sm font-medium text-n-slate-11">
                  {{ $t('STORAGE_MGMT.TOTAL_STORAGE') }}
                </div>
                <div class="mt-2 text-2xl font-bold text-n-slate-12">
                  {{ formatBytes(totalSize) }}
                </div>
                <div class="mt-1 text-xs text-n-slate-11">
                  {{
                    $t('STORAGE_MGMT.TOTAL_FILES', {
                      count: formatNumber(storageData.overview.total_blobs),
                    })
                  }}
                </div>
              </div>

              <div class="rounded-md border border-n-weak bg-n-solid-1 p-4">
                <div class="text-sm font-medium text-n-slate-11">
                  {{ $t('STORAGE_MGMT.ORPHAN_FILES') }}
                </div>
                <div class="mt-2 text-2xl font-bold text-n-slate-12">
                  {{ formatBytes(orphanSize) }}
                </div>
                <div class="mt-1 text-xs text-n-slate-11">
                  {{
                    $t('STORAGE_MGMT.ORPHAN_COUNT', {
                      count: formatNumber(storageData.orphan_blobs.count),
                    })
                  }}
                </div>
              </div>

              <div class="rounded-md border border-n-weak bg-n-solid-1 p-4">
                <div class="text-sm font-medium text-n-slate-11">
                  {{ $t('STORAGE_MGMT.DUPLICATE_WASTE') }}
                </div>
                <div class="mt-2 text-2xl font-bold text-n-slate-12">
                  {{ formatBytes(duplicateWasted) }}
                </div>
                <div class="mt-1 text-xs text-n-slate-11">
                  {{
                    $t('STORAGE_MGMT.DUPLICATE_COUNT', {
                      count: formatNumber(storageData.duplicates.files),
                    })
                  }}
                </div>
              </div>
            </div>

            <div
              v-if="potentialSavings > 0"
              class="mt-4 rounded-md border border-n-weak bg-n-solid-1 p-4"
            >
              <div class="flex items-center justify-between">
                <div>
                  <div class="text-sm font-medium text-n-slate-11">
                    {{ $t('STORAGE_MGMT.POTENTIAL_SAVINGS') }}
                  </div>
                  <div class="mt-1 text-xl font-bold text-n-slate-12">
                    {{ formatBytes(potentialSavings) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          class="flex w-full flex-col divide-y divide-n-weak rounded-xl bg-n-solid-2 outline outline-1 outline-n-container"
        >
          <div class="flex flex-col items-start gap-2 px-5 py-4">
            <h3 class="text-base font-medium text-n-slate-12">
              {{ $t('STORAGE_MGMT.BY_SOURCE_TITLE') }}
            </h3>
            <p class="mb-0 text-sm text-n-slate-11">
              {{ $t('STORAGE_MGMT.BY_SOURCE_SUBTITLE') }}
            </p>
          </div>

          <div class="px-5 py-4">
            <div class="space-y-3">
              <div
                v-for="(source, key) in storageData.by_source"
                :key="key"
                class="flex items-center justify-between rounded-md border border-n-weak bg-n-solid-1 p-3"
              >
                <div class="flex items-center gap-3">
                  <span
                    class="flex h-8 w-8 items-center justify-center rounded-md bg-n-alpha-2 text-n-slate-11"
                  >
                    <span
                      class="h-4 w-4"
                      :class="
                        key === 'messages'
                          ? 'i-lucide-message-square'
                          : key === 'canned_responses'
                            ? 'i-lucide-bookmark'
                            : 'i-lucide-file'
                      "
                    />
                  </span>
                  <div>
                    <div class="text-sm font-medium text-n-slate-12">
                      {{ $t(getSourceLabel(key)) }}
                    </div>
                    <div class="text-xs text-n-slate-11">
                      {{ formatNumber(source.count) }}
                      {{ $t('STORAGE_MGMT.FILES_LABEL') }}
                    </div>
                  </div>
                </div>
                <div class="text-sm font-semibold text-n-slate-12">
                  {{ formatBytes(source.size) }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          class="flex w-full flex-col divide-y divide-n-weak rounded-xl bg-n-solid-2 outline outline-1 outline-n-container"
        >
          <div class="flex flex-col items-start gap-2 px-5 py-4">
            <h3 class="text-base font-medium text-n-slate-12">
              {{ $t('STORAGE_MGMT.BY_TYPE_TITLE') }}
            </h3>
            <p class="mb-0 text-sm text-n-slate-11">
              {{ $t('STORAGE_MGMT.BY_TYPE_SUBTITLE') }}
            </p>
          </div>

          <div class="px-5 py-4">
            <div class="space-y-2">
              <div
                v-for="item in storageData.by_content_type.slice(0, 10)"
                :key="item.content_type"
                class="flex items-center justify-between rounded border border-n-weak bg-n-solid-1 p-2"
              >
                <div class="flex items-center gap-2">
                  <span class="text-xs text-n-slate-11">
                    {{
                      $t('STORAGE_MGMT.CONTENT_TYPE_COUNT', {
                        type: item.content_type,
                        count: formatNumber(item.count),
                      })
                    }}
                  </span>
                </div>
                <span class="text-sm font-medium text-n-slate-12">
                  {{ formatBytes(item.size) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div
          class="flex w-full flex-col divide-y divide-n-weak rounded-xl bg-n-solid-2 outline outline-1 outline-n-container"
        >
          <div class="flex flex-col items-start gap-2 px-5 py-4">
            <h3 class="text-base font-medium text-n-slate-12">
              {{ $t('STORAGE_MGMT.ACTIONS_TITLE') }}
            </h3>
            <p class="mb-0 text-sm text-n-slate-11">
              {{ $t('STORAGE_MGMT.ACTIONS_SUBTITLE') }}
            </p>
          </div>

          <div class="px-5 py-4">
            <div class="space-y-4">
              <div
                class="flex items-center justify-between rounded-md border border-n-weak bg-n-solid-1 p-4"
              >
                <div class="flex-1">
                  <h4 class="text-sm font-semibold text-n-slate-12">
                    {{ $t('STORAGE_MGMT.CLEANUP_ORPHANS_TITLE') }}
                  </h4>
                  <p class="mt-1 text-xs text-n-slate-11">
                    {{ $t('STORAGE_MGMT.CLEANUP_ORPHANS_DESC') }}
                  </p>
                </div>
                <div class="flex items-center gap-2">
                  <NextButton
                    v-if="!cleanupLoading"
                    :disabled="
                      !storageData.orphan_blobs.count ||
                      storageData.orphan_blobs.count === 0
                    "
                    size="small"
                    amber
                    @click="cleanupOrphans"
                  >
                    {{ $t('STORAGE_MGMT.CLEANUP_ACTION') }}
                  </NextButton>
                  <template v-else>
                    <Spinner size="small" />
                    <NextButton
                      :disabled="cancellingCleanup"
                      size="small"
                      ruby
                      @click="cancelCleanup"
                    >
                      {{ $t('STORAGE_MGMT.STOP_ACTION') }}
                    </NextButton>
                  </template>
                </div>
              </div>

              <div
                class="flex items-center justify-between rounded-md border border-n-weak bg-n-solid-1 p-4"
              >
                <div class="flex-1">
                  <h4 class="text-sm font-semibold text-n-slate-12">
                    {{ $t('STORAGE_MGMT.VIEW_DUPLICATES_TITLE') }}
                  </h4>
                  <p class="mt-1 text-xs text-n-slate-11">
                    {{ $t('STORAGE_MGMT.VIEW_DUPLICATES_DESC') }}
                  </p>
                </div>
                <NextButton
                  :disabled="
                    duplicatesLoading ||
                    !storageData.duplicates.files ||
                    storageData.duplicates.files === 0
                  "
                  size="small"
                  slate
                  @click="loadDuplicates"
                >
                  <span v-if="!duplicatesLoading">
                    {{ $t('STORAGE_MGMT.VIEW_ACTION') }}
                  </span>
                  <Spinner v-else size="small" />
                </NextButton>
              </div>

              <div
                class="flex items-center justify-between rounded-md border border-n-weak bg-n-solid-1 p-4"
              >
                <div class="flex-1">
                  <h4 class="text-sm font-semibold text-n-slate-12">
                    {{ $t('STORAGE_MGMT.LARGEST_FILES_TITLE') }}
                  </h4>
                  <p class="mt-1 text-xs text-n-slate-11">
                    {{ $t('STORAGE_MGMT.LARGEST_FILES_SUBTITLE') }}
                  </p>
                </div>
                <NextButton
                  :disabled="largestFilesLoading"
                  size="small"
                  slate
                  @click="loadLargestFiles"
                >
                  <span v-if="!largestFilesLoading">
                    {{ $t('STORAGE_MGMT.LARGEST_FILES_ACTION') }}
                  </span>
                  <Spinner v-else size="small" />
                </NextButton>
              </div>

              <div
                class="flex items-center justify-between rounded-md border border-n-weak bg-n-solid-1 p-4"
              >
                <div class="flex-1">
                  <h4 class="text-sm font-semibold text-n-slate-12">
                    {{ $t('STORAGE_MGMT.DEDUPLICATE_TITLE') }}
                  </h4>
                  <p class="mt-1 text-xs text-n-slate-11">
                    {{ $t('STORAGE_MGMT.DEDUPLICATE_DESC') }}
                  </p>
                </div>
                <div class="flex items-center gap-2">
                  <NextButton
                    v-if="!deduplicateLoading"
                    :disabled="
                      !storageData.duplicates.files ||
                      storageData.duplicates.files === 0
                    "
                    size="small"
                    ruby
                    @click="deduplicateFiles"
                  >
                    {{ $t('STORAGE_MGMT.DEDUPLICATE_ACTION') }}
                  </NextButton>
                  <template v-else>
                    <Spinner size="small" />
                    <NextButton
                      :disabled="cancellingDedup"
                      size="small"
                      ruby
                      @click="cancelDeduplication"
                    >
                      {{ $t('STORAGE_MGMT.STOP_ACTION') }}
                    </NextButton>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="showDuplicates && duplicatesList.length > 0"
          class="flex w-full flex-col divide-y divide-n-weak rounded-xl bg-n-solid-2 outline outline-1 outline-n-container"
        >
          <div class="flex flex-col items-start gap-2 px-5 py-4">
            <h3 class="text-base font-medium text-n-slate-12">
              {{ $t('STORAGE_MGMT.DUPLICATES_LIST_TITLE') }}
            </h3>
            <p class="mb-0 text-sm text-n-slate-11">
              {{
                $t('STORAGE_MGMT.DUPLICATES_LIST_SUBTITLE', {
                  count: duplicatesList.length,
                })
              }}
            </p>
          </div>

          <div class="px-5 py-4">
            <div class="space-y-2">
              <div
                v-for="(dup, index) in duplicatesList"
                :key="index"
                class="flex items-center justify-between rounded border border-n-weak bg-n-solid-1 p-3"
              >
                <div class="flex-1">
                  <div class="text-sm font-medium text-n-slate-12">
                    {{ dup.filename }}
                  </div>
                  <div
                    class="mt-1 flex items-center gap-3 text-xs text-n-slate-11"
                  >
                    <span>{{ dup.content_type }}</span>
                    <span>{{ $t('STORAGE_MGMT.SEPARATOR') }}</span>
                    <span>{{ dup.count }} {{ $t('STORAGE_MGMT.COPIES') }}</span>
                    <span>{{ $t('STORAGE_MGMT.SEPARATOR') }}</span>
                    <span>
                      {{ formatBytes(dup.total_size) }}
                      {{ $t('STORAGE_MGMT.TOTAL') }}
                    </span>
                  </div>
                </div>
                <div class="text-sm font-semibold text-n-slate-12">
                  {{ formatBytes(dup.wasted_space) }}
                  {{ $t('STORAGE_MGMT.WASTED') }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="showLargestFiles && largestFilesList.length > 0"
          class="flex w-full flex-col divide-y divide-n-weak rounded-xl bg-n-solid-2 outline outline-1 outline-n-container"
        >
          <div class="flex flex-col items-start gap-2 px-5 py-4">
            <h3 class="text-base font-medium text-n-slate-12">
              {{ $t('STORAGE_MGMT.LARGEST_FILES_TITLE') }}
            </h3>
            <p class="mb-0 text-sm text-n-slate-11">
              {{ $t('STORAGE_MGMT.LARGEST_FILES_SUBTITLE') }}
            </p>
          </div>

          <div class="px-5 py-4">
            <div class="space-y-2">
              <div
                v-for="(file, index) in largestFilesList"
                :key="index"
                class="flex items-center justify-between rounded border border-n-weak bg-n-solid-1 p-3"
              >
                <div class="flex-1">
                  <div class="flex items-center gap-2">
                    <div class="text-sm font-medium text-n-slate-12">
                      {{ file.filename }}
                    </div>
                    <span
                      v-if="file.is_duplicate"
                      class="rounded bg-n-ruby-3 px-2 py-0.5 text-xs text-n-ruby-11"
                    >
                      {{
                        $t('STORAGE_MGMT.FILE_IS_DUPLICATE', {
                          count: file.duplicate_count,
                        })
                      }}
                    </span>
                  </div>
                  <div
                    class="mt-1 flex items-center gap-3 text-xs text-n-slate-11"
                  >
                    <span>{{ file.content_type }}</span>
                    <span>{{ $t('STORAGE_MGMT.SEPARATOR') }}</span>
                    <span>
                      {{ $t('STORAGE_MGMT.FILE_CREATED') }}
                      {{ formatDate(file.created_at) }}
                    </span>
                  </div>
                </div>
                <div class="text-sm font-semibold text-n-slate-12">
                  {{ formatBytes(file.size) }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <woot-confirm-modal
      ref="cleanupConfirmDialog"
      :title="$t('STORAGE_MGMT.CLEANUP_ORPHANS_TITLE')"
      :description="cleanupConfirmMessage"
    />
    <woot-confirm-modal
      ref="deduplicateConfirmDialog"
      :title="$t('STORAGE_MGMT.DEDUPLICATE_TITLE')"
      :description="deduplicateConfirmMessage"
    />
  </SettingsLayout>
</template>
